import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "contexts/AuthContext";
import { fetchMemberWorkspaces } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";

const WorkspaceContext = createContext(null);

const GLOBAL_SELECTED_WORKSPACE_KEY = "selectedWorkspaceId";
const GLOBAL_SELECTED_WORKSPACE_READY_KEY = "selectedWorkspaceReady";
const storageKeyFor = (userId) => `selectedWorkspaceId:${userId || "anonymous"}`;
const workspaceUseStorageKeyFor = (workspaceId) => `selectedWorkspaceUse:${workspaceId || ""}`;
const workspaceListStorageKeyFor = (userId) => `memberWorkspaces:${userId || "anonymous"}`;
const WORKSPACE_LIST_CACHE_TTL_MS = 5 * 60 * 1000;
const workspaceFetchPromises = new Map();

const readWorkspaceListCache = (userId) => {
  try {
    const cached = JSON.parse(
      localStorage.getItem(workspaceListStorageKeyFor(userId)) || "null",
    );

    if (
      cached?.actorId === userId &&
      Array.isArray(cached?.workspaces) &&
      Date.now() - Number(cached?.savedAt || 0) < WORKSPACE_LIST_CACHE_TTL_MS
    ) {
      return cached.workspaces;
    }
  } catch {
    // Ignore malformed cache and fetch a fresh list.
  }

  return null;
};

const writeWorkspaceListCache = (userId, workspaces) => {
  try {
    localStorage.setItem(
      workspaceListStorageKeyFor(userId),
      JSON.stringify({
        actorId: userId,
        savedAt: Date.now(),
        workspaces,
      }),
    );
  } catch {
    // A cache miss is acceptable if storage is unavailable or full.
  }
};

const accountModeOf = (workspace) =>
  String(workspace?.accountType || "").toUpperCase() === "ORGANIZATION" ||
  Boolean(workspace?.organizationId)
    ? "ORGANIZATION"
    : "INDIVIDUAL";

const defaultWorkspaceUse = (workspace) =>
  accountModeOf(workspace) === "ORGANIZATION" ? "HIRING" : "JOB_SEARCH";

const normalizeWorkspaceUse = (value, workspace) => {
  if (accountModeOf(workspace) === "ORGANIZATION") return "HIRING";
  return String(value || "").toUpperCase() === "HIRING" ? "HIRING" : "JOB_SEARCH";
};

const WorkspaceProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const actorId = user?.id || user?.userId || "";
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [selectedWorkspaceUse, setSelectedWorkspaceUse] = useState("JOB_SEARCH");
  const [workspaceUseLoading, setWorkspaceUseLoading] = useState(false);
  const [workspaceUseReady, setWorkspaceUseReady] = useState(false);
  const [workspaceListReady, setWorkspaceListReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const selectedWorkspace = useMemo(
    () =>
      workspaces.find((account) => account?.id === selectedWorkspaceId) ||
      null,
    [workspaces, selectedWorkspaceId],
  );

  const selectWorkspace = useCallback(
    (accountId) => {
      const nextId = accountId || "";
      setSelectedWorkspaceId(nextId);

      if (actorId) {
        const key = storageKeyFor(actorId);
        if (nextId) {
          localStorage.setItem(key, nextId);
          localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_KEY, nextId);
        } else {
          localStorage.removeItem(key);
          localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
        }
        localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      }
    },
    [actorId],
  );

  const applyWorkspaceSelection = useCallback(
    (accounts) => {
      const savedId = localStorage.getItem(storageKeyFor(actorId));
      const nextSelectedId =
        accounts.find((account) => account?.id === savedId)?.id ||
        accounts[0]?.id ||
        "";

      setWorkspaces(accounts);
      setSelectedWorkspaceId(nextSelectedId);
      if (nextSelectedId) {
        localStorage.setItem(storageKeyFor(actorId), nextSelectedId);
        localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_KEY, nextSelectedId);
      } else {
        localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      }
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      setWorkspaceListReady(true);

      return accounts;
    },
    [actorId],
  );

  const refreshWorkspaces = useCallback(async ({ preferCache = false } = {}) => {
    if (!isAuthenticated || !actorId) {
      setWorkspaceListReady(false);
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      setSelectedWorkspaceUse("JOB_SEARCH");
      localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      return [];
    }

    if (preferCache) {
      const cachedAccounts = readWorkspaceListCache(actorId);
      if (cachedAccounts) {
        setError(null);
        setLoading(false);
        return applyWorkspaceSelection(cachedAccounts);
      }
    }

    try {
      setLoading(true);
      setWorkspaceListReady(false);
      setError(null);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "0");
      const fetchKey = `${actorId}:AUTO_WEB_UI`;
      let fetchPromise = workspaceFetchPromises.get(fetchKey);
      if (!fetchPromise) {
        fetchPromise = fetchMemberWorkspaces({
          actorId,
          page: 0,
          size: 100,
        }).finally(() => {
          workspaceFetchPromises.delete(fetchKey);
        });
        workspaceFetchPromises.set(fetchKey, fetchPromise);
      }

      const page = await fetchPromise;
      const accounts = page.content || [];
      writeWorkspaceListCache(actorId, accounts);
      return applyWorkspaceSelection(accounts);
    } catch (nextError) {
      setError(nextError);
      setWorkspaces([]);
      setSelectedWorkspaceId("");
      setSelectedWorkspaceUse("JOB_SEARCH");
      localStorage.removeItem(GLOBAL_SELECTED_WORKSPACE_KEY);
      localStorage.setItem(GLOBAL_SELECTED_WORKSPACE_READY_KEY, "1");
      setWorkspaceListReady(true);
      return [];
    } finally {
      setLoading(false);
    }
  }, [actorId, applyWorkspaceSelection, isAuthenticated]);

  useEffect(() => {
    refreshWorkspaces({ preferCache: true });
  }, [refreshWorkspaces]);

  useEffect(() => {
    let cancelled = false;

    const loadWorkspaceUse = async () => {
      setWorkspaceUseReady(false);
      setWorkspaceUseLoading(true);

      if (!selectedWorkspaceId || !selectedWorkspace) {
        setSelectedWorkspaceUse("JOB_SEARCH");
        setWorkspaceUseLoading(false);
        setWorkspaceUseReady(true);
        return;
      }

      if (accountModeOf(selectedWorkspace) === "ORGANIZATION") {
        setSelectedWorkspaceUse("HIRING");
        setWorkspaceUseLoading(false);
        setWorkspaceUseReady(true);
        return;
      }

      try {
        const record = await olreadiaService.workspaceUses.getForWorkspace(
          selectedWorkspaceId,
          actorId,
        );
        if (!cancelled) {
          setSelectedWorkspaceUse(
            normalizeWorkspaceUse(record?.workspaceUse, selectedWorkspace),
          );
        }
      } catch {
        if (!cancelled) {
          const cachedUse = localStorage.getItem(workspaceUseStorageKeyFor(selectedWorkspaceId));
          setSelectedWorkspaceUse(
            cachedUse
              ? normalizeWorkspaceUse(cachedUse, selectedWorkspace)
              : defaultWorkspaceUse(selectedWorkspace),
          );
        }
      } finally {
        if (!cancelled) {
          setWorkspaceUseLoading(false);
          setWorkspaceUseReady(true);
        }
      }
    };

    loadWorkspaceUse();

    return () => {
      cancelled = true;
    };
  }, [actorId, selectedWorkspace, selectedWorkspaceId]);

  const value = useMemo(
    () => ({
      workspaces,
      selectedWorkspace,
      selectedWorkspaceId,
      selectedWorkspaceUse,
      workspaceAccountMode: accountModeOf(selectedWorkspace),
      workspaceWorkMode: selectedWorkspaceUse === "HIRING" ? "EMPLOYER" : "PROFESSIONAL",
      isOrganizationWorkspace:
        Boolean(selectedWorkspace) && accountModeOf(selectedWorkspace) === "ORGANIZATION",
      isIndividualWorkspace:
        Boolean(selectedWorkspace) && accountModeOf(selectedWorkspace) === "INDIVIDUAL",
      isEmployerWorkspace: selectedWorkspaceUse === "HIRING",
      isProfessionalWorkspace: selectedWorkspaceUse === "JOB_SEARCH",
      selectWorkspace,
      refreshWorkspaces,
      workspaceUseLoading,
      workspaceUseReady,
      workspaceListReady,
      loading,
      error,
    }),
    [
      error,
      loading,
      workspaces,
      refreshWorkspaces,
      selectWorkspace,
      selectedWorkspace,
      selectedWorkspaceId,
      selectedWorkspaceUse,
      workspaceUseLoading,
      workspaceUseReady,
      workspaceListReady,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within WorkspaceProvider");
  }
  return context;
};

export default WorkspaceProvider;
